// Organization-aware auth storage and management

export interface OrganizationAuth {
  organization_id: string;
  organization_name?: string;
  accessToken: string;
  refreshToken: string;
  lastUsed?: number; // timestamp for tracking usage
}

export interface FlikAuthData {
  currentOrganization: string | null;
  organizations: OrganizationAuth[];
}

export interface OrganizationAuthStorage {
  get: () => FlikAuthData | null;
  set: (data: FlikAuthData) => void;
  remove: () => void;
  updateOrganization: (orgId: string, auth: Partial<OrganizationAuth>) => void;
  removeOrganization: (orgId: string) => void;
  setCurrentOrganization: (orgId: string | null) => void;
}

// Default storage key for organization auth data
export const FLIK_AUTH_STORAGE_KEY = 'flik_auth';

// Create organization-aware storage
export const createOrganizationAuthStorage = (): OrganizationAuthStorage => {
  const storage: OrganizationAuthStorage = {
    get: (): FlikAuthData | null => {
      try {
        const stored = localStorage.getItem(FLIK_AUTH_STORAGE_KEY);
        if (!stored) return null;

        const data = JSON.parse(stored) as FlikAuthData;

        // Validate the structure
        if (!data.organizations || !Array.isArray(data.organizations)) {
          return null;
        }

        return data;
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        return null;
      }
    },

    set: (data: FlikAuthData): void => {
      try {
        localStorage.setItem(FLIK_AUTH_STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.error('Error storing auth data:', error);
      }
    },

    remove: (): void => {
      localStorage.removeItem(FLIK_AUTH_STORAGE_KEY);
    },

    updateOrganization: (
      orgId: string,
      auth: Partial<OrganizationAuth>,
    ): void => {
      const currentData = storage.get() || {
        currentOrganization: null,
        organizations: [],
      };

      const existingIndex = currentData.organizations.findIndex(
        (org) => org.organization_id === orgId,
      );

      if (existingIndex >= 0) {
        // Update existing organization
        currentData.organizations[existingIndex] = {
          ...currentData.organizations[existingIndex],
          ...auth,
          lastUsed: Date.now(),
        };
      } else {
        // Add new organization
        currentData.organizations.push({
          organization_id: orgId,
          accessToken: auth.accessToken || '',
          refreshToken: auth.refreshToken || '',
          organization_name: auth.organization_name,
          lastUsed: Date.now(),
        });
      }

      storage.set(currentData);
    },

    removeOrganization: (orgId: string): void => {
      const currentData = storage.get();
      if (!currentData) return;

      currentData.organizations = currentData.organizations.filter(
        (org) => org.organization_id !== orgId,
      );

      // If we removed the current organization, clear it
      if (currentData.currentOrganization === orgId) {
        currentData.currentOrganization =
          currentData.organizations.length > 0
            ? currentData.organizations[0].organization_id
            : null;
      }

      storage.set(currentData);
    },

    setCurrentOrganization: (orgId: string | null): void => {
      const currentData = storage.get() || {
        currentOrganization: null,
        organizations: [],
      };

      // Validate that the organization exists
      if (
        orgId &&
        !currentData.organizations.find((org) => org.organization_id === orgId)
      ) {
        console.warn(`Organization ${orgId} not found in stored organizations`);
        return;
      }

      currentData.currentOrganization = orgId;

      // Update lastUsed timestamp for the organization
      if (orgId) {
        const orgIndex = currentData.organizations.findIndex(
          (org) => org.organization_id === orgId,
        );
        if (orgIndex >= 0) {
          currentData.organizations[orgIndex].lastUsed = Date.now();
        }
      }

      storage.set(currentData);
    },
  };

  return storage;
};

// Helper functions for working with organization auth
export const getCurrentOrganizationAuth = (
  storage: OrganizationAuthStorage,
): OrganizationAuth | null => {
  const data = storage.get();
  if (!data || !data.currentOrganization) return null;

  return (
    data.organizations.find(
      (org) => org.organization_id === data.currentOrganization,
    ) || null
  );
};

export const getAllOrganizationAuths = (
  storage: OrganizationAuthStorage,
): OrganizationAuth[] => {
  const data = storage.get();
  return data?.organizations || [];
};

export const hasOrganizationAuth = (
  storage: OrganizationAuthStorage,
  orgId: string,
): boolean => {
  const data = storage.get();
  return (
    data?.organizations.some((org) => org.organization_id === orgId) || false
  );
};
